import {
  EyeOutlined,
  FileTextOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { Button, Card, Descriptions, Space, Tag, Typography } from 'antd';
import React, { useState } from 'react';
import {
  DATE_FORMATS,
  PLAN_STATUS,
  ROAD_MAP_ALLOWED_FORMATS,
} from '../../../../../../common/constants';
import {
  formatDate,
  getPlayPlanLabel,
  humanizeEnum,
} from '../../../../../../common/utils';
import UploadFileModal from '../../../../../../components/UploadFileModal';
import useUltraPlanRoadmapDocument from '../../../../hooks/useUltraPlanRoadmapDocument';
import { getFullName } from '../../../../ultraPlanUtils';

const { Text } = Typography;

const formatDateValue = (value) =>
  value ? formatDate(value, DATE_FORMATS?.REGULAR) : '-';

const STATUS_COLORS = {
  [PLAN_STATUS?.ACTIVE?.value]: 'green',
  [PLAN_STATUS?.EXPIRED?.value]: 'default',
  [PLAN_STATUS?.CANCELLED?.value]: 'red',
  [PLAN_STATUS?.FAILED?.value]: 'red',
  [PLAN_STATUS?.PENDING?.value]: 'gold',
  [PLAN_STATUS?.CREATED?.value]: 'blue',
};

export const openRoadmapDocument = (url) =>
  // eslint-disable-next-line no-undef
  window?.open(url, '_blank', 'noopener,noreferrer');

const SubscriberOverview = ({ subscription, loading, onDocumentSaved }) => {
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const roadmapDocument = subscription?.metaData?.ultraPlanRoadMap;

  const {
    getRoadmapSignedPutUrl,
    saveRoadmapDocument,
  } = useUltraPlanRoadmapDocument({
    userSubscriptionId: subscription?.id,
    onSaved: onDocumentSaved,
  });

  return (
    <>
      <Card title="Subscriber" loading={loading}>
        <Descriptions
          size="small"
          column={{ xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
        >
          <Descriptions.Item label="Name">
            {getFullName(subscription?.user)}
          </Descriptions.Item>
          <Descriptions.Item label="User Number">
            {subscription?.user?.userNumber || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Contact">
            {subscription?.user?.contactNo || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {subscription?.user?.email || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            {subscription?.status ? (
              <Tag color={STATUS_COLORS?.[subscription?.status]}>
                {PLAN_STATUS?.[subscription?.status]?.text ||
                  humanizeEnum(subscription?.status)}
              </Tag>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Plan">
            {subscription?.subscriptionPlan
              ? getPlayPlanLabel(subscription?.subscriptionPlan) || '-'
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Billing">
            {subscription?.subscriptionPlan?.type
              ? humanizeEnum(subscription?.subscriptionPlan?.type)
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Validity">
            {`${formatDateValue(subscription?.startedAt)} → ${formatDateValue(
              subscription?.expireAt,
            )}`}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <Card title="Roadmap File" loading={loading}>
        <div className="roadmap-file-row">
          <Space>
            <FileTextOutlined className="roadmap-file-icon" />
            <div>
              <Text strong>
                {roadmapDocument ? 'Roadmap file attached' : 'No file attached'}
              </Text>
              <div>
                <Text type="secondary">
                  The document shared with the parent (PDF or image).
                </Text>
              </div>
            </div>
            {roadmapDocument && <Tag color="green">Attached</Tag>}
          </Space>
          {subscription?.id && (
            <Space wrap>
              {roadmapDocument && (
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => openRoadmapDocument(roadmapDocument)}
                >
                  View
                </Button>
              )}
              <Button
                type={roadmapDocument ? 'default' : 'primary'}
                icon={<PaperClipOutlined />}
                onClick={() => setIsDocumentModalOpen(true)}
              >
                {roadmapDocument ? 'Replace' : 'Attach File'}
              </Button>
            </Space>
          )}
        </div>
      </Card>
      {isDocumentModalOpen && (
        <UploadFileModal
          isModalOpen={isDocumentModalOpen}
          setIsModalOpen={setIsDocumentModalOpen}
          title={`${roadmapDocument ? 'Replace' : 'Attach'} Roadmap File`}
          okText={roadmapDocument ? 'Replace' : 'Attach'}
          accept={ROAD_MAP_ALLOWED_FORMATS}
          existingFileUrl={roadmapDocument}
          existingFileLabel="A roadmap file is already attached."
          getSignedPutUrl={getRoadmapSignedPutUrl}
          onUploaded={saveRoadmapDocument}
        />
      )}
    </>
  );
};

export default SubscriberOverview;
